import { Book, Gift, Send, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { CreateYourMemoriesCard } from "../components/CreateYourMemoriesCard";
import {
  createSubmitAnecdoteFormData,
  useAnecdoteByToken,
  useSubmitAnecdote,
} from "../hooks/useAnecdotes";
import { logo } from "../images";

type SubmissionState = "form" | "success";

export const ContributeAnecdote = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(search).get("token");

  const [submissionState, setSubmissionState] =
    useState<SubmissionState>("form");
  const [content, setContent] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Fetch anecdote data by token
  const {
    data: anecdoteData,
    isLoading,
    error,
  } = useAnecdoteByToken(token || "");

  const submitMutation = useSubmitAnecdote();

  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [token, navigate]);

  // Check if anecdote already has content
  useEffect(() => {
    if (anecdoteData?.anecdote?.content) {
      setSubmissionState("success");
    }
  }, [anecdoteData]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !content.trim()) {
      return;
    }

    const formData = createSubmitAnecdoteFormData(
      token,
      content,
      marketingAccepted,
      photo || undefined
    );

    submitMutation.mutate(formData, {
      onSuccess: () => {
        setSubmissionState("success");
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-delicate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-delicate-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-nunito">
            {t("anecdotes.contribute.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (error || !anecdoteData) {
    return (
      <div className="min-h-screen bg-delicate-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              alt="Memoirs logo"
              src={logo.logoGrey}
              className="mx-auto h-16 w-auto mb-4"
            />
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Expired Card */}
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl font-poppins font-bold text-rust-600 mb-4">
                  {t("anecdotes.contribute.expired.title")}
                </h2>
                <p className="text-gray-600 mb-4 text-base sm:text-lg font-nunito">
                  {t("anecdotes.contribute.expired.description")}
                </p>
                <p className="text-gray-600 mb-6 text-base sm:text-lg font-nunito">
                  {t("anecdotes.contribute.expired.contact", {
                    author: anecdoteData?.book?.author || "l'auteur",
                  })}
                </p>
              </div>
            </div>

            {/* Créez vos propres mémoires - Title */}
            <h3 className="text-xl sm:text-2xl font-poppins font-bold text-gray-900 mb-6 text-center">
              {t("anecdotes.contribute.expired.createYourMemoirs")}
            </h3>

            {/* Two Cards Side by Side */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Démarrer memoirs Card */}
              <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-delicate-100 rounded-full flex items-center justify-center mb-4 border border-gray-300">
                  <Book className="w-8 h-8 sm:w-10 sm:h-10 text-delicate-600" />
                </div>
                <h4 className="text-lg sm:text-xl font-poppins font-semibold text-gray-900 mb-2 text-center">
                  {t("anecdotes.contribute.expired.startMemoirs")}
                </h4>
                <p className="text-gray-600 mb-6 text-sm sm:text-base font-nunito text-center flex-grow">
                  {t("anecdotes.contribute.expired.startMemoirsDescription")}
                </p>
                <a
                  href={import.meta.env.VITE_MEMOIRS_BASE_URL || "/"}
                  className="w-full px-4 py-2.5 bg-sand-500 text-white rounded-md hover:bg-sand-600 transition-colors text-center font-medium shadow-sm font-poppins"
                >
                  {t("anecdotes.contribute.expired.startMemoirs")}
                </a>
              </div>

              {/* Offrir memoirs Card */}
              <div className="bg-sand-10 rounded-lg shadow-md p-6 flex flex-col items-center border border-sand-100">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-sand-200 rounded-full flex items-center justify-center mb-4">
                  <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-sand-600" />
                </div>
                <h4 className="text-lg sm:text-xl font-poppins font-semibold text-gray-900 mb-2 text-center">
                  {t("anecdotes.contribute.expired.giftMemoirs")}
                </h4>
                <p className="text-gray-600 mb-6 text-sm sm:text-base font-nunito text-center flex-grow">
                  {t("anecdotes.contribute.expired.giftMemoirsDescription")}
                </p>
                <a
                  href={import.meta.env.VITE_MEMOIRS_BASE_URL || "/"}
                  className="w-full px-4 py-2.5 bg-delicate-500 text-white rounded-md hover:bg-delicate-600 transition-colors text-center font-medium shadow-sm font-poppins"
                >
                  {t("anecdotes.contribute.expired.giftMemoirs")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { anecdote, chapter, book } = anecdoteData;

  return (
    <div className="min-h-screen bg-delicate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            alt="Memoirs logo"
            src={logo.logoGrey}
            className="mx-auto h-16 w-auto mb-4"
          />
        </div>

        {submissionState === "form" ? (
          <>
            <div className="max-w-3xl mx-auto">
              {/* FORM VIEW */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden p-8">
                {/* Header Section */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-rust-600 mb-3">
                    <Book size={24} />
                    <h1 className="text-xl font-poppins font-bold">
                      {chapter.title}
                    </h1>
                  </div>
                  <p className="text-gray-600 font-nunito">
                    {t("anecdotes.contribute.form.invitedBy", {
                      author: book.author,
                    })}
                  </p>
                </div>

                {/* Content Section */}
                <div className="">
                  <div className="mb-8">
                    {/* Custom Message */}
                    {anecdote.custom_message && (
                      <div className="bg-delicate-10 rounded-lg p-6 mb-6 border border-delicate-200">
                        <p className="text-gray-700 whitespace-pre-line leading-relaxed font-nunito">
                          {anecdote.custom_message}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Content Textarea */}
                    <div>
                      <label
                        htmlFor="content"
                        className="block text-sm font-medium text-gray-700 mb-2 font-poppins"
                      >
                        {t("anecdotes.contribute.form.yourAnecdote")}{" "}
                        <span className="text-rust-500">
                          {t("anecdotes.contribute.form.required")}
                        </span>
                      </label>
                      <textarea
                        id="content"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={10}
                        required
                        placeholder={t("anecdotes.contribute.form.placeholder")}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:border-delicate-500 resize-none font-nunito"
                      />
                      <p className="mt-2 text-sm text-gray-500 font-nunito">
                        {t("anecdotes.contribute.form.minLength")}
                      </p>
                      <div className="mt-1 text-right">
                        <span className="text-xs text-gray-500 font-nunito">
                          {t("anecdotes.contribute.form.charCount", {
                            count: content.length,
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Photo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                        {t("anecdotes.contribute.form.addPhoto")}
                      </label>
                      <div className="mt-2">
                        {photoPreview ? (
                          <div className="relative">
                            <img
                              src={photoPreview}
                              alt="Preview"
                              className="w-full max-h-96 object-contain rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setPhoto(null);
                                setPhotoPreview(null);
                              }}
                              className="absolute top-2 right-2 bg-rust-500 text-white p-2 rounded-full hover:bg-rust-600 transition-colors shadow-md"
                            >
                              <X size={20} />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-delicate-300 rounded-lg cursor-pointer hover:bg-delicate-10 transition-colors bg-white">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <div className="w-12 h-12 bg-delicate-100 rounded-full flex items-center justify-center mb-3">
                                <Upload className="w-6 h-6 text-delicate-600" />
                              </div>
                              <p className="text-sm text-gray-600 font-nunito mb-1">
                                {t("anecdotes.contribute.form.addImage")}
                              </p>
                              <p className="text-xs text-gray-500 font-nunito">
                                {t("anecdotes.contribute.form.imageCount")}
                              </p>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-gray-500 font-nunito">
                        {t("anecdotes.contribute.form.imageFormat")}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 font-nunito">
                        {t("anecdotes.contribute.form.imageQuality")}
                      </p>
                    </div>

                    {/* Marketing Consent */}
                    <div className="flex items-start gap-3 bg-delicate-10 p-4 rounded-lg border border-delicate-200">
                      <input
                        type="checkbox"
                        id="consent"
                        required
                        checked={consentAccepted}
                        onChange={(e) => setConsentAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 text-delicate-600 focus:ring-delicate-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="consent"
                        className="text-sm text-gray-700 font-nunito"
                      >
                        {t("anecdotes.contribute.form.consentText", {
                          author: book.author,
                        })}
                      </label>
                    </div>
                    <div className="flex items-start gap-3 bg-delicate-10 p-4 rounded-lg border border-delicate-200">
                      <input
                        type="checkbox"
                        id="marketing"
                        checked={marketingAccepted}
                        onChange={(e) => setMarketingAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 text-delicate-600 focus:ring-delicate-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="marketing"
                        className="text-sm text-gray-700 font-nunito"
                      >
                        {t("anecdotes.contribute.form.marketingConsent")}
                      </label>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={submitMutation.isLoading || !content.trim()}
                        className="w-full px-6 py-3 bg-rust-500 text-white font-semibold rounded-lg hover:bg-rust-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md font-poppins"
                      >
                        {submitMutation.isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            {t("anecdotes.contribute.form.submitting")}
                          </>
                        ) : (
                          <>
                            <Send size={20} />
                            {t("anecdotes.contribute.form.submit")}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="max-w-full">
              {/* SUCCESS VIEW */}
              {/* Confirmation Card */}
              <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-poppins font-bold text-rust-400 mb-4">
                    {t("anecdotes.contribute.success.title")}
                  </h2>
                  <p className="text-gray-600 text-base sm:text-lg font-nunito mb-6">
                    {t("anecdotes.contribute.success.description", {
                      author: book.author || book.author,
                    })}
                  </p>
                </div>
              </div>

              {/* Two Cards Side by Side */}
              <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 lg:space-x-6">
                <CreateYourMemoriesCard />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
